import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useCookies } from "react-cookie";

// bootstrap imports
import Container from "react-bootstrap/Container";
import { Button, Badge, Form } from "react-bootstrap";
import { Pagination } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";

// app-specific
import Table from "../components/Table";
import NavBar from "../components/NavBar";
import { DailyCallsExamTypes } from "../constants/apps";
import { NavigateRoutes } from "../constants/routes";

import {
  IoInformationCircleOutline,
  IoVideocam,
  IoTrash,
  IoTime,
  IoCloseCircle,
  IoPersonRemove,
} from "react-icons/io5";

// utils
import { tokenIsValid, decode, getTokenExpirationDate } from "../utils/token";

import {
  getAllDailyCallsExams,
  deleteDailyCallsExam,
  cancelDailyCallsExam,
  markDailyCallsExamNoShow,
  rescheduleDailyCallsExam,
} from "../api/DailyCallsExams.js";

// custom page styling
import styles from "../styles/Admin.module.css";

const PAGE_SIZE = 10;
const BATCH_SIZE = 50;
const REFRESH_INTERVAL_MS = 300000; // 5 minutes, matching the trainings page

// fixed so every exam type is always offered, whether or not any exam
// currently uses it; "All types" is the default and applies no filter
const ALL_EXAM_TYPES = "All types";
const EXAM_TYPE_OPTIONS = [...DailyCallsExamTypes, ALL_EXAM_TYPES];

const STATUS_CANCELLED = "cancelled";
const STATUS_NO_SHOW = "no_show";
const isCancelled = (record) => record.status === STATUS_CANCELLED;
const isNoShow = (record) => record.status === STATUS_NO_SHOW;
// an exam that is not going ahead as it stands
const isSettled = (record) => isCancelled(record) || isNoShow(record);

// how many earlier dates for this exam the RA missed, from its history
const missedBefore = (record) =>
  (record.reschedule_history || []).filter(
    (entry) => entry.previous_status === STATUS_NO_SHOW,
  ).length;

// Exam times are entered and shown in Central time, but the browser reads
// them as its own local time. That is exact for anyone working in Central time
// and off by the time zone difference for anyone who is not.
const examStart = (dateIso, timeIso) => {
  if (!dateIso || !timeIso) return null;
  const start = new Date(`${dateIso}T${timeIso}`);
  return Number.isNaN(start.getTime()) ? null : start;
};

const isPast = (record) => {
  const start = examStart(record.exam_date_iso, record.exam_start_time_iso);
  return start !== null && start < new Date();
};

// minutes since midnight for an HH:MM time input value, or null
const minutesOf = (hhmm) => {
  const match = /^(\d{2}):(\d{2})$/.exec(hhmm || "");
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

const hhmmOf = (minutes) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(
    minutes % 60,
  ).padStart(2, "0")}`;

const RESCHEDULE_UNCHANGED = "Change the date or a time to reschedule.";

/**
 * Page component for displaying scheduled daily calls exams.
 * Mirrors ExistingInterviewsPage, with a start and end time in place of a
 * single interview time.
 * @returns React.FC
 */
const ExistingDailyCallsExamsPage = () => {
  // state - the page of exams currently displayed
  const [exams, setExams] = useState([]);
  const [curPage, setCurPage] = useState(1);
  const [totalPage, setTotalPage] = useState();
  const [selectedExamType, setSelectedExamType] = useState(ALL_EXAM_TYPES);

  // client-side cache with lazy loading
  const [examCache, setExamCache] = useState([]);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [isRefreshingCache, setIsRefreshingCache] = useState(false);
  const [hasMoreExams, setHasMoreExams] = useState(false);
  const [examsOffset, setExamsOffset] = useState(0);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [loadError, setLoadError] = useState("");

  // state - get user info
  const [userData, setUserData] = useState();

  // state - whether the user is able to access this page
  const [authorized, setAuthorized] = useState();
  const [reauthorized, setReauthorized] = useState(false);

  // info modal
  const [modalShow, setModalShow] = useState(false);
  const [modalHeader, setModalHeader] = useState("");
  const [modalBody, setModalBody] = useState("");

  // delete confirmation
  const [deleteConfirmShow, setDeleteConfirmShow] = useState(false);
  const [deleteExamData, setDeleteExamData] = useState(null);
  const [isDeletingExam, setIsDeletingExam] = useState(false);

  // cancel confirmation
  const [cancelConfirmShow, setCancelConfirmShow] = useState(false);
  const [cancelExamData, setCancelExamData] = useState(null);
  const [isCancellingExam, setIsCancellingExam] = useState(false);
  const [cancelError, setCancelError] = useState("");

  // no show confirmation
  const [noShowConfirmShow, setNoShowConfirmShow] = useState(false);
  const [noShowExamData, setNoShowExamData] = useState(null);
  const [isMarkingNoShow, setIsMarkingNoShow] = useState(false);
  const [noShowError, setNoShowError] = useState("");

  // reschedule popup
  const [rescheduleShow, setRescheduleShow] = useState(false);
  const [rescheduleExamData, setRescheduleExamData] = useState(null);
  const [newExamDate, setNewExamDate] = useState("");
  const [newExamStartTime, setNewExamStartTime] = useState("");
  const [newExamEndTime, setNewExamEndTime] = useState("");
  const [isReschedulingExam, setIsReschedulingExam] = useState(false);
  const [rescheduleError, setRescheduleError] = useState("");

  // navigate hook
  const navigate = useNavigate();

  // cookies hook
  const [cookies] = useCookies(["token", "firebaseToken"]);

  const redirectLogin = useCallback(
    () => navigate(NavigateRoutes.LOGIN + "?redirect=EXISTINGDAILYCALLSEXAMS"),
    [navigate],
  );

  // Initial authorization check
  useEffect(() => {
    document.title = "Project DUAL Apps: Existing Daily Calls Exams";
    const token = cookies["token"];

    if (token && tokenIsValid(token)) {
      const decoded = decode(token);

      if (decoded) {
        setUserData(decoded);
        setAuthorized(true);
        setReauthorized(true);
      }
    } else {
      setAuthorized(false);
    }
  }, [cookies]);

  // =================================
  // -- Data loading --
  // =================================

  const fetchAndCacheAllExams = useCallback(() => {
    setIsRefreshingCache(true);
    setLoadError("");

    return getAllDailyCallsExams(cookies["token"], BATCH_SIZE, 0)
      .then((res) => {
        if (res.data.success) {
          setExamCache(res.data.data);
          setHasMoreExams(res.data.has_more);
          setExamsOffset(BATCH_SIZE);
          setLastRefreshTime(new Date());
        } else {
          setLoadError("The server could not return the exam list.");
        }
        setIsRefreshingCache(false);
      })
      .catch((err) => {
        console.error("Error fetching exams:", err);
        setLoadError(
          err.response?.data?.detail ||
            "Could not load exams. Please refresh and try again.",
        );
        setIsRefreshingCache(false);
      });
  }, [cookies]);

  const loadMoreExams = useCallback(() => {
    if (isFetchingMore || !hasMoreExams) return;

    setIsFetchingMore(true);
    getAllDailyCallsExams(cookies["token"], BATCH_SIZE, examsOffset)
      .then((res) => {
        if (res.data.success) {
          setExamCache((prevCache) => [...prevCache, ...res.data.data]);
          setHasMoreExams(res.data.has_more);
          setExamsOffset(examsOffset + BATCH_SIZE);
        }
        setIsFetchingMore(false);
      })
      .catch((err) => {
        console.error("Error loading more exams:", err);
        setIsFetchingMore(false);
      });
  }, [cookies, examsOffset, hasMoreExams, isFetchingMore]);

  // Fetch once the user is authorized
  useEffect(() => {
    if (reauthorized) {
      fetchAndCacheAllExams();
    }
  }, [reauthorized, fetchAndCacheAllExams]);

  // Filter the cache by the selected exam type
  const getFilteredExams = useCallback(() => {
    if (selectedExamType === ALL_EXAM_TYPES) {
      return examCache;
    }
    return examCache.filter((record) => record.exam_type === selectedExamType);
  }, [examCache, selectedExamType]);

  const getPaginatedFilteredData = useCallback(
    (page, pageSize) => {
      const filteredData = getFilteredExams();
      if (filteredData.length === 0) return { data: [], totalPage: 0 };

      const totalPages = Math.ceil(filteredData.length / pageSize);

      if (page > totalPages) {
        return { data: [], totalPage: totalPages };
      }

      const startIdx = (page - 1) * pageSize;

      return {
        data: filteredData.slice(startIdx, startIdx + pageSize),
        totalPage: totalPages,
      };
    },
    [getFilteredExams],
  );

  // Update the displayed page whenever the cache, page, or filters change
  useEffect(() => {
    if (reauthorized) {
      const paginatedResult = getPaginatedFilteredData(curPage, PAGE_SIZE);
      setExams(paginatedResult.data);
      setTotalPage(paginatedResult.totalPage);
    }
  }, [reauthorized, examCache, curPage, getPaginatedFilteredData]);

  // Auto-refresh the cache on a timer, as the trainings page does
  useEffect(() => {
    if (reauthorized) {
      const timer = setInterval(
        () => fetchAndCacheAllExams(),
        REFRESH_INTERVAL_MS,
      );
      return () => clearInterval(timer);
    }
  }, [reauthorized, fetchAndCacheAllExams]);

  // Token expiry
  useEffect(() => {
    const exp = getTokenExpirationDate(cookies["token"]);
    if (exp) {
      const now = new Date();
      const newTimeout = setTimeout(
        () => setAuthorized(false),
        exp - now - 10 * 1000,
      );
      return () => clearTimeout(newTimeout);
    }
  }, [cookies]);

  // Redirect to login when unauthorized
  useEffect(() => {
    if (authorized === false) {
      const timeout = setTimeout(() => redirectLogin(), 5000);
      return () => clearTimeout(timeout);
    }
  }, [authorized, redirectLogin]);

  // =================================
  // -- Button handlers --
  // =================================

  const infoExam = (record) => {
    setModalHeader("Exam Details");
    setModalBody(
      <Container>
        <p>
          <b>RA</b>
          <br />
          {record.ra_full_name}
          <br />
          {record.ra_email}
          <br />
          {record.ra_phone}
        </p>
        <hr />
        <p>
          <b>Exam Administrator</b>
          <br />
          {record.administrator_full_name}
          <br />
          {record.administrator_email}
          <br />
          {record.administrator_phone}
        </p>
        <hr />
        <p>
          <b>Exam</b>
          <br />
          {record.exam_type}
          <br />
          {record.exam_date}, {record.exam_start_time} -{" "}
          {record.exam_end_time}
        </p>
        <hr />
        <p>
          <b>Zoom</b>
          <br />
          <a
            href={record.administrator_zoom_url}
            target="_blank"
            rel="noreferrer"
          >
            {record.administrator_zoom_url}
          </a>
        </p>
        {record.scheduler_email && (
          <>
            <hr />
            <p>
              <b>Scheduler</b>
              <br />
              {record.scheduler_email}
            </p>
          </>
        )}
        {isCancelled(record) && (
          <>
            <hr />
            <p>
              <b>This exam has been cancelled.</b>
            </p>
          </>
        )}
        {isNoShow(record) && (
          <>
            <hr />
            <p>
              <b>The RA did not show up for this exam.</b>
              <br />
              They have been asked to book a make-up with the Scheduling Team.
              Rescheduling this exam books that make-up and clears the no show.
            </p>
          </>
        )}
        {missedBefore(record) > 0 && !isNoShow(record) && (
          <>
            <hr />
            <p>
              <b>
                Previously a no show{" "}
                {missedBefore(record) > 1 && `(${missedBefore(record)} times)`}
              </b>
              <br />
              The dates the RA missed are marked below.
            </p>
          </>
        )}
        {(record.reschedule_history || []).length > 0 && (
          <>
            <hr />
            <p>
              <b>Previously scheduled for</b>
              {record.reschedule_history.map((entry, idx) => (
                <React.Fragment key={idx}>
                  <br />
                  {entry.previous_exam_date}, {entry.previous_exam_start_time}{" "}
                  - {entry.previous_exam_end_time}
                  {entry.previous_status === STATUS_NO_SHOW && (
                    <> (no show)</>
                  )}
                </React.Fragment>
              ))}
            </p>
          </>
        )}
      </Container>,
    );
    setModalShow(true);
  };

  const joinExam = (record) => {
    if (!record.administrator_zoom_url) {
      setModalHeader("No Zoom Link");
      setModalBody(
        <Container>
          <p>This exam has no Zoom URL on file.</p>
        </Container>,
      );
      setModalShow(true);
      return;
    }
    window.open(record.administrator_zoom_url, "_blank", "noreferrer");
  };

  const handleDeleteClick = (record) => {
    setDeleteExamData(record);
    setDeleteConfirmShow(true);
  };

  const handleConfirmDelete = () => {
    if (!deleteExamData) return;

    setIsDeletingExam(true);
    deleteDailyCallsExam(deleteExamData.id, cookies["token"])
      .then(() => {
        setIsDeletingExam(false);
        setDeleteConfirmShow(false);
        setDeleteExamData(null);
        fetchAndCacheAllExams();
      })
      .catch((err) => {
        console.error("Error deleting exam:", err);
        setIsDeletingExam(false);
        setDeleteConfirmShow(false);
        setModalHeader("Error");
        setModalBody(
          <Container>
            <p>
              Failed to delete exam:{" "}
              {err.response?.data?.detail || err.message || "Unknown error"}
            </p>
          </Container>,
        );
        setModalShow(true);
      });
  };

  const showResult = (header, message) => {
    setModalHeader(header);
    setModalBody(
      <Container>
        <p>{message}</p>
      </Container>,
    );
    setModalShow(true);
  };

  const handleCancelClick = (record) => {
    setCancelExamData(record);
    setCancelError("");
    setCancelConfirmShow(true);
  };

  const closeCancel = () => {
    // once the emails are on their way, let the request finish
    if (isCancellingExam) return;
    setCancelConfirmShow(false);
    setCancelExamData(null);
    setCancelError("");
  };

  const handleConfirmCancel = () => {
    if (!cancelExamData || isCancellingExam) return;
    const record = cancelExamData;

    setIsCancellingExam(true);
    setCancelError("");
    cancelDailyCallsExam(record.id, cookies["token"])
      .then(() => {
        setIsCancellingExam(false);
        setCancelConfirmShow(false);
        setCancelExamData(null);
        showResult(
          "Exam Cancelled",
          `The exam for ${record.ra_full_name} on ${record.exam_date} at ${record.exam_start_time} is cancelled. ${record.ra_full_name} and ${record.administrator_full_name} have been emailed.`,
        );
        fetchAndCacheAllExams();
      })
      .catch((err) => {
        console.error("Error cancelling exam:", err);
        setIsCancellingExam(false);
        setCancelError(
          `Could not cancel the exam: ${
            err.response?.data?.detail || err.message || "Unknown error"
          }`,
        );
        // someone else may have changed it in the meantime
        fetchAndCacheAllExams();
      });
  };

  const handleNoShowClick = (record) => {
    setNoShowExamData(record);
    setNoShowError("");
    setNoShowConfirmShow(true);
  };

  const closeNoShow = () => {
    // once the email is on its way, let the request finish
    if (isMarkingNoShow) return;
    setNoShowConfirmShow(false);
    setNoShowExamData(null);
    setNoShowError("");
  };

  const handleConfirmNoShow = () => {
    if (!noShowExamData || isMarkingNoShow) return;
    const record = noShowExamData;

    setIsMarkingNoShow(true);
    setNoShowError("");
    markDailyCallsExamNoShow(record.id, cookies["token"])
      .then(() => {
        setIsMarkingNoShow(false);
        setNoShowConfirmShow(false);
        setNoShowExamData(null);
        showResult(
          "Marked as No Show",
          `${record.ra_full_name} has been emailed that they missed their ${record.exam_type} exam on ${record.exam_date} at ${record.exam_start_time}, and asked to book a make-up with the Scheduling Team. ${record.administrator_full_name} has been told the RA did not attend.`,
        );
        fetchAndCacheAllExams();
      })
      .catch((err) => {
        console.error("Error marking no show:", err);
        setIsMarkingNoShow(false);
        setNoShowError(
          `Could not mark this exam as a no show: ${
            err.response?.data?.detail || err.message || "Unknown error"
          }`,
        );
        // someone else may have changed it in the meantime
        fetchAndCacheAllExams();
      });
  };

  const handleRescheduleClick = (record) => {
    setRescheduleExamData(record);
    setNewExamDate(record.exam_date_iso || "");
    setNewExamStartTime(record.exam_start_time_iso || "");
    setNewExamEndTime(record.exam_end_time_iso || "");
    setRescheduleError("");
    setRescheduleShow(true);
  };

  const closeReschedule = () => {
    // once the emails are on their way, let the request finish
    if (isReschedulingExam) return;
    setRescheduleShow(false);
    setRescheduleExamData(null);
    setRescheduleError("");
  };

  // Moving the start time moves the end time with it, so the exam keeps its
  // length unless the end time is changed as well
  const handleNewStartTimeChange = (value) => {
    const oldStart = minutesOf(newExamStartTime);
    const oldEnd = minutesOf(newExamEndTime);
    const start = minutesOf(value);
    if (oldStart !== null && oldEnd !== null && start !== null && oldEnd > oldStart) {
      const end = start + (oldEnd - oldStart);
      if (end < 24 * 60) setNewExamEndTime(hhmmOf(end));
    }
    setNewExamStartTime(value);
  };

  // why the entered date and times cannot be submitted yet, or "" if they can
  const getRescheduleProblem = () => {
    if (!rescheduleExamData) return "";
    if (!newExamDate || !newExamStartTime || !newExamEndTime) {
      return "Enter a new date, start time and end time.";
    }
    if (
      newExamDate === rescheduleExamData.exam_date_iso &&
      newExamStartTime === rescheduleExamData.exam_start_time_iso &&
      newExamEndTime === rescheduleExamData.exam_end_time_iso
    ) {
      return RESCHEDULE_UNCHANGED;
    }
    const start = examStart(newExamDate, newExamStartTime);
    if (start === null || minutesOf(newExamEndTime) === null) {
      return "That date or time is not valid.";
    }
    if (minutesOf(newExamEndTime) <= minutesOf(newExamStartTime)) {
      return "The end time must be after the start time.";
    }
    if (start < new Date()) return "The new date and time are in the past.";
    return "";
  };

  const handleConfirmReschedule = () => {
    if (
      !rescheduleExamData ||
      isReschedulingExam ||
      getRescheduleProblem()
    ) {
      return;
    }
    const record = rescheduleExamData;

    setIsReschedulingExam(true);
    setRescheduleError("");
    rescheduleDailyCallsExam(
      record.id,
      newExamDate,
      newExamStartTime,
      newExamEndTime,
      cookies["token"],
    )
      .then((res) => {
        setIsReschedulingExam(false);
        setRescheduleShow(false);
        setRescheduleExamData(null);
        showResult(
          res.data.was_no_show ? "Make-up Exam Booked" : "Exam Rescheduled",
          `The exam for ${record.ra_full_name} moved from ${record.exam_date}, ${record.exam_start_time} - ${record.exam_end_time} to ${res.data.exam_date}, ${res.data.exam_start_time} - ${res.data.exam_end_time}. ${record.ra_full_name} and ${record.administrator_full_name} have been emailed.` +
            (res.data.was_no_show
              ? " The no show has been lifted, and the missed date is kept in Exam Details."
              : ""),
        );
        fetchAndCacheAllExams();
      })
      .catch((err) => {
        console.error("Error rescheduling exam:", err);
        setIsReschedulingExam(false);
        setRescheduleError(
          `Could not reschedule the exam: ${
            err.response?.data?.detail || err.message || "Unknown error"
          }`,
        );
        // someone else may have changed it in the meantime
        fetchAndCacheAllExams();
      });
  };

  // =================================
  // -- Rendering helpers --
  // =================================

  const ExamRow = (props) => {
    const record = props.record;
    const cancelled = isCancelled(record);
    const noShow = isNoShow(record);
    const settled = isSettled(record);
    const past = isPast(record);
    const rescheduled = (record.reschedule_history || []).length > 0;

    let cancelTitle = "Cancel this exam and email the RA and exam administrator";
    if (cancelled) cancelTitle = "This exam is already cancelled";
    else if (noShow) cancelTitle = "This exam is marked as a no show";
    else if (past) cancelTitle = "This exam is in the past, so it cannot be cancelled";

    let noShowTitle =
      "Mark the RA as a no show and email them and the exam administrator";
    if (noShow) noShowTitle = "This exam is already marked as a no show";
    else if (cancelled) noShowTitle = "This exam is cancelled";
    else if (!past) noShowTitle = "This exam has not started yet";

    return (
      <tr style={settled ? { opacity: 0.55 } : undefined}>
        <td>{record.exam_type}</td>
        <td>{record.ra_full_name}</td>
        <td>{record.administrator_full_name}</td>
        <td>
          {record.exam_date}
          {cancelled && (
            <>
              {" "}
              <Badge bg="secondary">Cancelled</Badge>
            </>
          )}
          {noShow && (
            <>
              {" "}
              <Badge bg="dark">No show</Badge>
            </>
          )}
          {!settled && rescheduled && (
            <>
              {" "}
              <Badge bg="primary">Rescheduled</Badge>
            </>
          )}
        </td>
        <td>
          {record.exam_start_time} - {record.exam_end_time}
        </td>
        <td>
          <div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => infoExam(record)}
            >
              <IoInformationCircleOutline />
            </Button>
          </div>
        </td>
        <td>
          <div
            title={
              settled
                ? "This exam is not going ahead"
                : "Open the Zoom link for this exam"
            }
          >
            <Button
              variant="primary"
              size="sm"
              disabled={settled}
              onClick={() => joinExam(record)}
            >
              <IoVideocam />
            </Button>
          </div>
        </td>
        <td>
          <div
            title={
              cancelled
                ? "This exam is cancelled"
                : noShow
                  ? "Book the make-up by moving this exam to a new date and time"
                  : "Move this exam to a new date and time and email the RA and exam administrator"
            }
          >
            <Button
              variant="primary"
              size="sm"
              disabled={cancelled}
              onClick={() => handleRescheduleClick(record)}
            >
              <IoTime />
            </Button>
          </div>
        </td>
        <td>
          <div title={cancelTitle}>
            <Button
              variant="warning"
              size="sm"
              disabled={settled || past}
              onClick={() => handleCancelClick(record)}
            >
              <IoCloseCircle />
            </Button>
          </div>
        </td>
        <td>
          <div title={noShowTitle}>
            <Button
              variant="dark"
              size="sm"
              disabled={settled || !past}
              onClick={() => handleNoShowClick(record)}
            >
              <IoPersonRemove />
            </Button>
          </div>
        </td>
        {userData && userData.is_admin && (
          <td>
            <div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleDeleteClick(record)}
                title="Permanently delete this record. Nobody is emailed."
              >
                <IoTrash />
              </Button>
            </div>
          </td>
        )}
      </tr>
    );
  };

  const handleChangePage = useCallback(
    (page) => {
      const itemsNeeded = page * PAGE_SIZE;

      if (itemsNeeded > examCache.length && hasMoreExams) {
        loadMoreExams();
      }

      setCurPage(page);
    },
    [examCache.length, hasMoreExams, loadMoreExams],
  );

  function MyPagination({ total, current, onChangePage }) {
    let items = [];

    if (current > 1) {
      items.push(
        <Pagination.Prev
          key="prev"
          onClick={() => onChangePage(current - 1)}
        />,
      );
    }

    for (let page = 1; page <= total; page++) {
      items.push(
        <Pagination.Item
          key={page}
          data-page={page}
          active={page === current}
          onClick={() => onChangePage(page)}
        >
          {page}
        </Pagination.Item>,
      );
    }

    if (current < total) {
      items.push(
        <Pagination.Next
          key="next"
          onClick={() => onChangePage(current + 1)}
        />,
      );
    }

    return <Pagination>{items}</Pagination>;
  }

  const handleExamTypeSort = (info) => {
    setSelectedExamType(info);
    setCurPage(1);
  };

  const baseColumns = [
    "Exam Type",
    "RA Name",
    "Exam Administrator",
    "Day to Meet",
    "Time to Meet (in CST)",
    "Exam Info",
    "Join Exam",
    "Reschedule",
    "Cancel Exam",
    "No Show",
  ];
  const columns =
    userData && userData.is_admin
      ? [...baseColumns, "Delete Record"]
      : baseColumns;

  const rescheduleProblem = getRescheduleProblem();

  // only the first column is filterable, matching the RA interviews table
  const sortable = columns.map((_, idx) => idx === 0);
  const handleSorted = columns.map((_, idx) =>
    idx === 0 ? handleExamTypeSort : false,
  );

  // return for the page
  return (
    <div>
      <NavBar />
      <Container className={styles.pageContainer}>
        <Container>
          <h1>Existing Daily Calls Exams Page</h1>
        </Container>

        <Container className={styles.sectionContainer}>
          {isRefreshingCache && examCache.length === 0 && reauthorized && (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p style={{ marginTop: "15px", color: "#666" }}>
                Loading exams...
              </p>
            </div>
          )}

          {loadError && (
            <div style={{ textAlign: "center", padding: "20px", color: "#a00" }}>
              {loadError}
            </div>
          )}

          {reauthorized && !isRefreshingCache && !loadError && (
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "15px",
                }}
              >
                <h4 style={{ margin: 0 }}>Scheduled Daily Calls Exams</h4>
                <div>
                  <small style={{ marginRight: "10px", color: "#666" }}>
                    Last updated:{" "}
                    {lastRefreshTime
                      ? lastRefreshTime.toLocaleTimeString()
                      : "Loading..."}
                  </small>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => fetchAndCacheAllExams()}
                    disabled={isRefreshingCache}
                  >
                    {isRefreshingCache ? "Refreshing..." : "Refresh"}
                  </Button>
                </div>
              </div>

              <div
                className={
                  exams.length === 0 ? styles.emptyTableMinHeightExams : undefined
                }
              >
                <Table
                  columns={columns}
                  sortable={sortable}
                  handleSorted={handleSorted}
                  filters={[selectedExamType]}
                  all={EXAM_TYPE_OPTIONS}
                  rows={
                    exams.length > 0
                      ? exams.map((record) => (
                          <ExamRow record={record} key={record.id} />
                        ))
                      : [
                          <tr key="empty">
                            <td
                              colSpan={columns.length}
                              style={{ textAlign: "center", color: "#666" }}
                            >
                              {examCache.length === 0
                                ? "No daily calls exams have been scheduled yet."
                                : `No ${selectedExamType} exams are scheduled.`}
                            </td>
                          </tr>,
                        ]
                  }
                />
              </div>

              {totalPage > 1 && (
                <MyPagination
                  total={totalPage}
                  current={curPage}
                  onChangePage={handleChangePage}
                />
              )}

              {isFetchingMore && (
                <div style={{ textAlign: "center", padding: "15px" }}>
                  <Spinner
                    animation="border"
                    size="sm"
                    role="status"
                    style={{ marginRight: "10px" }}
                  />
                  <span style={{ color: "#666" }}>Loading more exams...</span>
                </div>
              )}

              {hasMoreExams && !isFetchingMore && (
                <div style={{ textAlign: "center", padding: "15px" }}>
                  <Button
                    variant="outline-primary"
                    onClick={() => loadMoreExams()}
                    disabled={isFetchingMore}
                  >
                    Load More Exams
                  </Button>
                </div>
              )}
            </div>
          )}
        </Container>
      </Container>

      <Modal
        show={modalShow}
        onHide={() => setModalShow(false)}
        backdrop={undefined}
      >
        <Modal.Header closeButton={true}>
          <Modal.Title>{modalHeader}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalBody}</Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteConfirmShow}
        onHide={() => {
          setDeleteConfirmShow(false);
          setDeleteExamData(null);
        }}
      >
        <Modal.Header closeButton={true}>
          <Modal.Title>Delete Exam Record</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteExamData && (
            <Container>
              <p>
                Permanently delete the exam record for{" "}
                <b>{deleteExamData.ra_full_name}</b> on{" "}
                <b>
                  {deleteExamData.exam_date} at{" "}
                  {deleteExamData.exam_start_time}
                </b>
                ?
              </p>
              <p>
                Use this for past exams, test entries and mistakes. The record
                is removed from this page for good.
              </p>
              <p style={{ color: "#a00" }}>
                Nobody is emailed.
                {!isCancelled(deleteExamData) &&
                  !isPast(deleteExamData) &&
                  " To let the RA and exam administrator know this exam is off, close this and use Cancel Exam instead."}
              </p>
            </Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setDeleteConfirmShow(false);
              setDeleteExamData(null);
            }}
            disabled={isDeletingExam}
          >
            Keep record
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={isDeletingExam}
          >
            {isDeletingExam ? "Deleting..." : "Delete record"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal
        show={cancelConfirmShow}
        onHide={closeCancel}
        backdrop={isCancellingExam ? "static" : true}
      >
        <Modal.Header closeButton={!isCancellingExam}>
          <Modal.Title>Cancel Exam</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cancelExamData && (
            <Container>
              <p>
                Cancel the exam for <b>{cancelExamData.ra_full_name}</b> with{" "}
                <b>{cancelExamData.administrator_full_name}</b> on{" "}
                <b>
                  {cancelExamData.exam_date} at{" "}
                  {cancelExamData.exam_start_time}
                </b>
                ?
              </p>
              <p>
                This emails both of them that the exam is cancelled. The exam
                stays on this page, marked Cancelled.
                {cancelExamData.scheduler_email &&
                  ` ${cancelExamData.scheduler_email} is copied on the exam administrator's email.`}
              </p>
              {cancelError && <p style={{ color: "#a00" }}>{cancelError}</p>}
            </Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeCancel}
            disabled={isCancellingExam}
          >
            Keep exam
          </Button>
          <Button
            variant="warning"
            onClick={handleConfirmCancel}
            disabled={isCancellingExam}
          >
            {isCancellingExam
              ? "Sending emails..."
              : "Cancel exam and email both"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* No Show Confirmation Modal */}
      <Modal
        show={noShowConfirmShow}
        onHide={closeNoShow}
        backdrop={isMarkingNoShow ? "static" : true}
      >
        <Modal.Header closeButton={!isMarkingNoShow}>
          <Modal.Title>Mark as No Show</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {noShowExamData && (
            <Container>
              <p>
                Did <b>{noShowExamData.ra_full_name}</b> miss their{" "}
                <b>{noShowExamData.exam_type}</b> exam on{" "}
                <b>
                  {noShowExamData.exam_date} at{" "}
                  {noShowExamData.exam_start_time}
                </b>
                ?
              </p>
              <p>
                This emails the RA that they did not show up and asks them to
                book a make-up with the Scheduling Team via Teams, and tells
                the exam administrator the RA did not attend. The exam stays on
                this page, marked No show, and rescheduling it later books the
                make-up.
                {noShowExamData.scheduler_email &&
                  ` ${noShowExamData.scheduler_email} is copied on the exam administrator's email.`}
              </p>
              {noShowError && <p style={{ color: "#a00" }}>{noShowError}</p>}
            </Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeNoShow}
            disabled={isMarkingNoShow}
          >
            They were there
          </Button>
          <Button
            variant="dark"
            onClick={handleConfirmNoShow}
            disabled={isMarkingNoShow}
          >
            {isMarkingNoShow
              ? "Sending emails..."
              : "Mark no show and email both"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reschedule Modal */}
      <Modal
        show={rescheduleShow}
        onHide={closeReschedule}
        backdrop={isReschedulingExam ? "static" : true}
      >
        <Modal.Header closeButton={!isReschedulingExam}>
          <Modal.Title>Reschedule Exam</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rescheduleExamData && (
            <Container>
              <p>
                <b>{rescheduleExamData.ra_full_name}</b> with{" "}
                <b>{rescheduleExamData.administrator_full_name}</b>
                <br />
                Currently {rescheduleExamData.exam_date},{" "}
                {rescheduleExamData.exam_start_time} -{" "}
                {rescheduleExamData.exam_end_time} (CST)
              </p>
              <Form onSubmit={(e) => e.preventDefault()}>
                <Form.Group className="mb-3" controlId="rescheduleDate">
                  <Form.Label>New date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newExamDate}
                    onChange={(e) => setNewExamDate(e.target.value)}
                    disabled={isReschedulingExam}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="rescheduleStartTime">
                  <Form.Label>New start time (CST)</Form.Label>
                  <Form.Control
                    type="time"
                    value={newExamStartTime}
                    onChange={(e) => handleNewStartTimeChange(e.target.value)}
                    disabled={isReschedulingExam}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="rescheduleEndTime">
                  <Form.Label>New end time (CST)</Form.Label>
                  <Form.Control
                    type="time"
                    value={newExamEndTime}
                    onChange={(e) => setNewExamEndTime(e.target.value)}
                    disabled={isReschedulingExam}
                    required
                  />
                </Form.Group>
              </Form>
              {rescheduleProblem && (
                <p
                  style={{
                    color:
                      rescheduleProblem === RESCHEDULE_UNCHANGED
                        ? "#666"
                        : "#a00",
                  }}
                >
                  {rescheduleProblem}
                </p>
              )}
              <p>
                This emails the RA and the exam administrator the new date and
                time. The exam administrator stays the same.
                {rescheduleExamData.scheduler_email &&
                  ` ${rescheduleExamData.scheduler_email} is copied on the exam administrator's email.`}
              </p>
              {isNoShow(rescheduleExamData) && (
                <p>
                  This exam is marked as a no show. Booking it here is the
                  make-up: the exam counts as scheduled again and the No show
                  label comes off, with the missed date kept in Exam Details.
                </p>
              )}
              {rescheduleError && (
                <p style={{ color: "#a00" }}>{rescheduleError}</p>
              )}
            </Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeReschedule}
            disabled={isReschedulingExam}
          >
            Keep current time
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmReschedule}
            disabled={isReschedulingExam || Boolean(rescheduleProblem)}
          >
            {isReschedulingExam
              ? "Sending emails..."
              : "Reschedule and email both"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ExistingDailyCallsExamsPage;
